import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Logo } from "@/components/common/Logo";
import { cn } from "@/lib/utils";

export function AuthLayout({ children, eyebrow, headline, sub, side }) {
  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden grid lg:grid-cols-[minmax(0,1fr)_1.1fr] bg-white text-slate-900">
      <div className="flex min-h-dvh min-w-0 flex-col px-5 py-8 sm:px-8 sm:py-10 md:px-14">
        <div className="shrink-0">
          <Logo size={26} />
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full min-w-0 py-8 sm:py-10">
          {eyebrow && (
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-indigo-600 mb-3">
              {eyebrow}
            </div>
          )}
          {headline && (
            <h1 className="text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">
              {headline}
            </h1>
          )}
          {sub && (
            <p className="mt-2 text-[13px] sm:text-[14px] text-slate-500 max-w-sm leading-relaxed">
              {sub}
            </p>
          )}
          <div className="mt-6 sm:mt-8 min-w-0 w-full">{children}</div>
        </div>
        <div className="shrink-0 text-[11px] text-slate-400 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <span>© Sellyx, Inc. {new Date().getFullYear()}</span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/privacy" className="hover:text-slate-600 py-1">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-600 py-1">Terms</Link>
            <Link to="/security" className="hover:text-slate-600 py-1">Security</Link>
          </span>
        </div>
      </div>

      <div className="relative hidden lg:block overflow-hidden border-l border-slate-200 min-h-dvh">
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-violet-50",
          )}
          aria-hidden
        />
        <div className="absolute inset-0 bg-grid opacity-50" aria-hidden />
        <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-violet-200/30 blur-3xl" />

        <div className="relative h-full flex flex-col justify-center px-10 xl:px-14">
          {side ?? <DefaultSidePanel />}
        </div>
      </div>
    </div>
  );
}

function DefaultSidePanel() {
  return (
    <div className="max-w-lg">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-200 backdrop-blur">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        {" "}
        Live · 2,431 conversations handled today
      </div>
      <h2 className="mt-5 text-[32px] xl:text-[40px] font-semibold leading-[1.05] tracking-tight text-slate-900">
        The AI Revenue OS.
      </h2>
      <p className="mt-3 text-slate-600 max-w-md leading-relaxed">
        Sellyx consolidates inbound, outbound, signals, content, CRM and AI agents into one cockpit —
        with OpenClaw quietly running every conversation in the background.
      </p>

      <div className="mt-10 grid grid-cols-3 gap-3">
        {[
          { label: "Channels unified", value: "7" },
          { label: "AI resolution", value: "78%" },
          { label: "SLA p50", value: "42s" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-slate-200 bg-white/80 backdrop-blur px-3 py-3">
            <div className="text-[20px] font-semibold tracking-tight text-slate-900">{s.value}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-slate-500">
        <span>Trusted by revenue teams at</span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium text-slate-700">
          <span>Stripe</span>
          <span className="opacity-30">·</span>
          <span>Linear</span>
          <span className="opacity-30">·</span>
          <span>Vercel</span>
          <span className="opacity-30">·</span>
          <span>Ramp</span>
        </span>
      </div>
    </div>
  );
}

AuthLayout.propTypes = {
  children: PropTypes.node,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  sub: PropTypes.string,
  side: PropTypes.node,
};
